/**
 * API Configuration Utility
 * Provides consistent API URL handling across the application
 */

export class ApiConfigError extends Error {
   constructor(message: string = 'API configuration error. Please contact support.') {
      super(message);
      this.name = 'ApiConfigError';
   }
}

/**
 * Gets the API URL from environment variables
 * @throws {ApiConfigError} When REACT_APP_API_URL is not configured
 * @returns {string} The API base URL
 */
export const getApiUrl = (): string => {
   const apiUrl = process.env.REACT_APP_API_URL;

   if (!apiUrl) {
      console.error('❌ REACT_APP_API_URL environment variable is not configured');
      throw new ApiConfigError();
   }

   return apiUrl;
};

/**
 * Creates a full API endpoint URL
 * @param endpoint - The API endpoint path (e.g., '/api/trips/123')
 * @returns {string} The full API URL
 * @throws {ApiConfigError} When REACT_APP_API_URL is not configured
 */
export const createApiUrl = (endpoint: string): string => {
   const baseUrl = getApiUrl();
   const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
   return `${baseUrl}${cleanEndpoint}`;
};

/**
 * Checks if API is properly configured
 * @returns {boolean} True if API URL is configured
 */
export const isApiConfigured = (): boolean => {
   return !!process.env.REACT_APP_API_URL;
};
